/**
 * Transaction-detail modal.
 *
 * Opens when the user clicks/taps a row on the party-ledger page. Fetches
 * the assembled `EntryDetail` bundle from
 *   GET /api/ledger-entries/:id/detail
 * and dispatches on `source.kind` to render one of four panels.
 *
 * Layout: content-height bottom sheet on mobile, centered modal on `sm:`
 * and up (matches the PaymentDialog shell convention).
 */
import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import type { EntryDetail } from '../../api';
import {
  Button,
  ErrorBanner,
  Pill,
  Spinner,
} from '../../ui/primitives';
import { useReadOnly } from '../../ui/AppShell';
import { usePinGate } from '../../auth/pinGate';
import { Modal } from '../../ui/Modal';
import { PaymentPanel } from './PaymentPanel';
import { BatchPanel } from './BatchPanel';
import { AdjustmentPanel } from './AdjustmentPanel';
import { ReversalPanel } from './ReversalPanel';
import { AttachmentsStrip } from './AttachmentsStrip';
import type { VoidRedoTarget } from '../VoidRedoDialog';

export interface TransactionDetailModalProps {
  entryId: number;
  /** Party id of the ledger this modal was opened from; used by Open-other-side navigation. */
  currentPartyId: number;
  onClose: () => void;
  /**
   * Void / Void-and-re-enter for payment and adjustment sources is handled
   * by the hosting page (it closes this modal, shows the confirmation with
   * balance before/after, and reopens the create dialog prefilled for the
   * redo flavour). When absent, the buttons are hidden.
   */
  onVoidAction?: (target: VoidRedoTarget) => void;
}

export function TransactionDetailModal({
  entryId,
  currentPartyId,
  onClose,
  onVoidAction,
}: TransactionDetailModalProps) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const readOnly = useReadOnly();
  const { withPin } = usePinGate();
  const scrollRef = useRef<HTMLDivElement>(null);

  const detailQ = useQuery<EntryDetail>({
    queryKey: ['ledger-detail', entryId],
    queryFn: () => api.get<EntryDetail>(`/api/ledger-entries/${entryId}/detail`),
  });

  // Esc-to-close is provided by <Modal> — do NOT add a second listener here:
  // onClose is `nav(-1)`, so firing it twice pops two history entries and
  // overshoots off the ledger.

  const reverseEntryMut = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      api.post(`/api/ledger-entries/${id}/reverse`, { reason }),
    onSuccess: () => {
      void qc.invalidateQueries();
      onClose();
    },
  });

  const detail = detailQ.data;
  const isReversal = detail?.source.kind === 'reversal';
  const isManualEntry =
    detail !== undefined &&
    !isReversal &&
    detail.entry.sourcePaymentId === null &&
    detail.entry.sourceSettlementId === null &&
    detail.entry.sourceBatchId === null &&
    detail.entry.sourceAdjustmentId === null;

  const openOtherParty = (otherPartyId: number, otherEntryId: number) => {
    // Just navigate — do NOT call onClose() first. onClose is `nav(-1)`
    // (an async history pop) which would land AFTER this push and yank the
    // user back to the origin ledger. The URL change alone swaps the modal
    // to the other leg, and device-Back returns to the entry we came from.
    navigate(`/parties/${otherPartyId}/ledger?focus_entry=${otherEntryId}`);
  };

  return (
    <Modal onClose={onClose} size="lg">
        {/* Header */}
        <header className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-3">
          <div className="flex items-baseline gap-2">
            <Pill section="ledgers">Ledger</Pill>
            <h2 className="text-small font-semibold text-ink">
              Transaction details
            </h2>
            {detail && (
              <span className="text-caption text-ink-muted">
                · {detail.entry.effectiveDate} · {sourceLabel(detail)}
              </span>
            )}
          </div>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </header>

        {/* Body */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4">
          {detailQ.isLoading ? (
            <div className="flex items-center gap-2 text-small text-ink-muted">
              <Spinner /> loading…
            </div>
          ) : detailQ.error ? (
            <ErrorBanner>
              {detailQ.error instanceof ApiError
                ? detailQ.error.message
                : 'Failed to load transaction details.'}
            </ErrorBanner>
          ) : detail ? (
            <div className="space-y-4">
              {/* Source-specific panel */}
              {detail.source.kind === 'payment' && (
                <PaymentPanel
                  detail={detail}
                  source={detail.source}
                  currentPartyId={currentPartyId}
                  onOpenOtherParty={openOtherParty}
                />
              )}
              {detail.source.kind === 'batch' && (
                <BatchPanel detail={detail} source={detail.source} />
              )}
              {detail.source.kind === 'adjustment' && (
                <AdjustmentPanel
                  detail={detail}
                  source={detail.source}
                  currentPartyId={currentPartyId}
                  onOpenOtherParty={openOtherParty}
                />
              )}
              {detail.source.kind === 'reversal' && (
                <ReversalPanel detail={detail} source={detail.source} />
              )}
              {detail.source.kind === 'none' && (
                <div className="rounded-md border border-border bg-surface-raised p-3 text-small text-ink-muted">
                  No source information is available for this entry.
                </div>
              )}

              {/* Attachments */}
              {detail.attachments.length > 0 && (
                <AttachmentsStrip attachments={detail.attachments} />
              )}
            </div>
          ) : null}
        </div>

        {/* Actions */}
        {detail && !readOnly && (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-surface-raised px-4 py-3">
            {onVoidAction &&
              detail.source.kind === 'payment' &&
              !detail.source.payment.voidedAt &&
              !isReversal && (
                <VoidActionButtons
                  kind="payment"
                  sourceId={detail.source.payment.id}
                  onVoidAction={onVoidAction}
                />
              )}
            {onVoidAction &&
              detail.source.kind === 'adjustment' &&
              !detail.source.adjustment.voidedAt &&
              !isReversal && (
                <VoidActionButtons
                  kind="adjustment"
                  sourceId={detail.source.adjustment.id}
                  onVoidAction={onVoidAction}
                />
              )}
            {isManualEntry && !isReversal && (
              <Button
                variant="danger"
                onClick={() => {
                  const reason = prompt('Reason for reversing this entry?');
                  if (reason && reason.trim().length > 0) {
                    void withPin(() =>
                      reverseEntryMut.mutateAsync({
                        id: detail.entry.id,
                        reason: reason.trim(),
                      }),
                    );
                  }
                }}
                disabled={reverseEntryMut.isPending}
              >
                {reverseEntryMut.isPending ? <Spinner /> : 'Reverse entry'}
              </Button>
            )}
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </footer>
        )}
    </Modal>
  );
}

/**
 * "Void & re-enter" (fix a value mistake by re-keying it) next to plain
 * "Void". Both delegate to the hosting page's VoidRedoDialog flow.
 */
function VoidActionButtons({
  kind,
  sourceId,
  onVoidAction,
}: {
  kind: 'payment' | 'adjustment';
  sourceId: number;
  onVoidAction: (target: VoidRedoTarget) => void;
}) {
  return (
    <>
      <Button
        variant="secondary"
        onClick={() => onVoidAction({ kind, id: sourceId, mode: 'redo' })}
      >
        Void &amp; re-enter
      </Button>
      <Button
        variant="danger"
        onClick={() => onVoidAction({ kind, id: sourceId, mode: 'void' })}
      >
        Void {kind}
      </Button>
    </>
  );
}

function sourceLabel(detail: EntryDetail): string {
  switch (detail.source.kind) {
    case 'payment': return 'Payment';
    case 'batch': return 'Weekly P&L';
    case 'adjustment': return 'Adjustment';
    case 'reversal': return 'Reversal';
    case 'none':
    default:
      return detail.entry.type;
  }
}
